// ─── useWorkerFilters.js ──────────────────────────────────────────────────────
// Shared filter state + payload builders for WorkerOnboardingPage and
// SuperAdminDashboard.  Import the hook; call the appropriate builder when
// hitting each page's own API.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";

const PAGE_LIMIT = 20;

/**
 * @param {{
 *   fetchLocations: () => Promise,
 *   fetchSkills:    () => Promise,
 *   fetchCompaniesByLocations: (ids: string[]) => Promise,   // optional – only needed on Onboarding page
 *   fetchLastCallStatuses:     () => Promise,                 // optional – only needed on Onboarding page
 * fetchLastCallSubStatuses:     () => Promise,                 // optional – only needed on Onboarding page
 *   fetchSources:              () => Promise,                 // optional – fetches sources from API
 * }}
 */
export function useWorkerFilters({
  fetchLocations,
  fetchSkills,
  fetchCompaniesByLocations = null,
  fetchLastCallStatuses = null,
  fetchLastCallSubStatuses = null,
  fetchSources = null,
} = {}) {

  // ── Lookup objects ────────────────────────────────────────────────────────
  const [locationObjects, setLocationObjects]   = useState([]);
  const [skillObjects,    setSkillObjects]       = useState([]);
  const [companyObjects,  setCompanyObjects]     = useState([]);
  const [sourceObjects,   setSourceObjects]      = useState([]);

  // ── Dropdown option arrays ────────────────────────────────────────────────
  const [locationOptions,       setLocationOptions]       = useState([]);
  const [skillOptions,          setSkillOptions]          = useState([]);
  const [companyOptions,        setCompanyOptions]        = useState([]);
  const [sourceOptions,         setSourceOptions]         = useState([]);
  const [lastCallStatusOptions, setLastCallStatusOptions] = useState(["All"]);
  const [lastCallSubStatusOptions, setLastCallSubStatusOptions] = useState(["All"]);

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [fetchingLocations,  setFetchingLocations]  = useState(true);
  const [fetchingSkills,     setFetchingSkills]     = useState(true);
  const [fetchingCompanies,  setFetchingCompanies]  = useState(false);
  const [fetchingSources,    setFetchingSources]    = useState(!!fetchSources);
  const [fetchingStatuses,   setFetchingStatuses]   = useState(!!fetchLastCallStatuses);
  const [fetchingSubStatuses,   setFetchingSubStatuses]   = useState(!!fetchLastCallSubStatuses);
  const [statusFilterError,  setStatusFilterError]  = useState(false);
  const [subStatusFilterError,  setSubStatusFilterError]  = useState(false);


  

  // ── Filter state ──────────────────────────────────────────────────────────
  const [selectedLocations,      setSelectedLocations]      = useState([]);
  const [selectedSkills,         setSelectedSkills]         = useState([]);
  const [selectedWorkerTypes,    setSelectedWorkerTypes]    = useState(["Registered", "Unregistered"]);
  const [selectedGenders,        setSelectedGenders]        = useState(["(Blanks)", "Male", "Female", "Others"]);
  const [selectedSources,        setSelectedSources]        = useState([]);
  const [selectedAadhaar,        setSelectedAadhaar]        = useState("All");
   const [Highestqualification,  setHighestqualification]            = useState("All");
  const [selectedKyc,            setSelectedKyc]            = useState("All");
  const [selectedRefStatus,      setSelectedRefStatus]      = useState("All");
  const [selectedCallCategory,   setSelectedCallCategory]   = useState("All");
  const [selectedCompanyCheck,   setSelectedCompanyCheck]   = useState("All");
  const [selectedBlacklisted,    setSelectedBlacklisted]    = useState("All");
  const [selectedpaymentverification, setselectedpaymentverification] = useState("All");
  const [selectedLastCompanies,  setSelectedLastCompanies]  = useState([]);
  const [selectedAllTimeCompanies, setSelectedAllTimeCompanies] = useState([]);
  const [selectedLastCallStatus, setSelectedLastCallStatus] = useState("All");
    const [selectedLastCallSubStatus, setSelectedLastCallSubStatus] = useState("All");
  const [registeredOnRange,      setRegisteredOnRange]      = useState({ startDate: "", endDate: "" });
  const [lastWorkedOnRange,      setLastWorkedOnRange]      = useState({ startDate: "", endDate: "" });
  const [companyLastWorkedOnRange, setCompanyLastWorkedOnRange] = useState({ startDate: "", endDate: "" });
  const [activeCompanyFilter,    setActiveCompanyFilter]    = useState(null); // "last" | "allTime" | null

  const [selectedHasAppToken, setSelectedHasAppToken] = useState("All");
  const [shiftCountRange,     setShiftCountRange]     = useState({ min: "", max: "" });

  // ─── NEW FILTER STATE ─────────────────────────────────────────────────────
  const [selectedPoliceVerification, setSelectedPoliceVerification] = useState("All");
  const [selectedMarksheet, setSelectedMarksheet] = useState("All");  // ← Keep as marksheet
  const [selectedEshram, setSelectedEshram] = useState("All");

  // ── Derived ───────────────────────────────────────────────────────────────
  const companyFiltersEnabled  = selectedLocations.length > 0 && !fetchingCompanies;
  const activeCompanySelections =
    activeCompanyFilter === "last"    ? selectedLastCompanies :
    activeCompanyFilter === "allTime" ? selectedAllTimeCompanies : [];
  const companyCheckEnabled   = activeCompanySelections.length === 1;
  const selectedCompanyName   = companyCheckEnabled
    ? companyOptions.find(c => c._id === activeCompanySelections[0])?.displayName
    : null;

  // Reset company-dependent filters when company deselected
  useEffect(() => {
    if (!companyCheckEnabled) {
      setSelectedCompanyCheck("All");
      setCompanyLastWorkedOnRange({ startDate: "", endDate: "" });
    }
  }, [companyCheckEnabled]);

  // ── Load locations ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fetchLocations) return;
    let alive = true;
    (async () => {
      try {
        setFetchingLocations(true);
        const data = await fetchLocations();
        if (!alive || !data?.success) return;

        const objs = (data.locations ?? [])
          .map(loc => ({ _id: loc._id, name: (loc.name ?? "").trim() || "(Blanks)" }))
          .filter(l => l._id);

        const seen = new Set();
        const unique = objs.filter(l => { if (seen.has(l.name)) return false; seen.add(l.name); return true; });

        setLocationObjects(unique);
        setLocationOptions(unique.map(l => l.name));
        setSelectedLocations(unique.map(l => l.name).filter(n => n !== "(Blanks)"));
      } catch (e) {
        console.error("Failed to load locations:", e);
      } finally {
        if (alive) setFetchingLocations(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchLocations]);

  // ── Load skills ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fetchSkills) return;
    let alive = true;
    (async () => {
      try {
        setFetchingSkills(true);
        const data = await fetchSkills();
        if (!alive || !data?.success) return;

        const objs = (data.skills ?? []).map(s => {
          if (typeof s === "string") return { _id: null, name: s.trim() || "(Blanks)" };
          return { _id: s._id ?? null, name: (s.name || s.skill || "").trim() || "(Blanks)" };
        });

        const seen = new Map();
        objs.forEach(s => { if (!seen.has(s.name)) seen.set(s.name, s); });
        const unique = [...seen.values()];

        setSkillObjects(unique);
        setSkillOptions(unique.map(s => s.name));
        setSelectedSkills(unique.map(s => s.name));
      } catch (e) {
        console.error("Failed to load skills:", e);
      } finally {
        if (alive) setFetchingSkills(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchSkills]);

  // ── Load sources dynamically ───────────────────────────────────────────────
  useEffect(() => {
    if (!fetchSources) {
      // Fallback to hardcoded sources if API not provided
      const fallbackSources = ["(Blanks)", "App", "Field Agent", "WhatsApp"];
      setSourceOptions(fallbackSources);
      setSelectedSources(fallbackSources.filter(s => s !== "(Blanks)"));
      setSourceObjects(["App", "Field Agent", "WhatsApp"]);
      return;
    }
    
    let alive = true;
    (async () => {
      try {
        setFetchingSources(true);
        const data = await fetchSources();
        if (!alive) return;

        // Parse the response - adjust based on actual API response structure
        let sourcesList = [];
        if (data?.success && Array.isArray(data?.sources)) {
          sourcesList = data.sources;
        } else if (Array.isArray(data?.data)) {
          sourcesList = data.data;
        } else if (Array.isArray(data)) {
          sourcesList = data;
        } else if (data?.sources && Array.isArray(data.sources)) {
          sourcesList = data.sources;
        }

        // Format sources for dropdown
        const formattedSources = sourcesList.map(source => {
          if (typeof source === 'string') return source;
          return source.name || source.label || source.source || source.value || '';
        }).filter(Boolean);

        // Remove duplicates and add "(Blanks)" at the beginning
        const uniqueSources = [...new Set(formattedSources)];
        const finalSources = ["(Blanks)", ...uniqueSources];
        
        setSourceOptions(finalSources);
        setSourceObjects(uniqueSources);
        
        // Set default selection to all sources except "(Blanks)"
        setSelectedSources(finalSources.filter(s => s !== "(Blanks)"));
      } catch (e) {
        console.error("Failed to load sources:", e);
        // Fallback to hardcoded sources if API fails
        const fallbackSources = ["(Blanks)", "App", "Field Agent", "WhatsApp"];
        setSourceOptions(fallbackSources);
        setSourceObjects(["App", "Field Agent", "WhatsApp"]);
        setSelectedSources(fallbackSources.filter(s => s !== "(Blanks)"));
      } finally {
        if (alive) setFetchingSources(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchSources]);

  // ── Load companies when locations change ──────────────────────────────────
  useEffect(() => {
    if (!fetchCompaniesByLocations || !locationObjects.length) return;
    let alive = true;
    (async () => {
      if (!selectedLocations.length) {
        setCompanyObjects([]);
        setCompanyOptions([]);
        setSelectedLastCompanies([]);
        setSelectedAllTimeCompanies([]);
        setActiveCompanyFilter(null);
        setFetchingCompanies(false);
        return;
      }
      try {
        setFetchingCompanies(true);
        const locationIds = selectedLocations
          .map(name => locationObjects.find(l => l.name === name)?._id)
          .filter(Boolean);
        if (!locationIds.length) return;

        const data = await fetchCompaniesByLocations(locationIds);
        if (!alive) return;

        const list = data?.companies ?? data?.data ?? [];
        const objs = Array.isArray(list)
          ? list.map(c => ({
              _id: c._id ?? null,
              name: (c.name || "").trim() || "(Blanks)",
              code: c.companyCode || null,
              displayName: c.companyCode
                ? `${(c.name || "").trim()} (${c.companyCode})`
                : (c.name || "").trim() || "(Blanks)",
            })).filter(c => c._id)
          : [];

        setCompanyObjects(objs);
        setCompanyOptions(objs);
      } catch (e) {
        console.error("Failed to load companies:", e);
        if (alive) { setCompanyObjects([]); setCompanyOptions([]); }
      } finally {
        if (alive) setFetchingCompanies(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedLocations, locationObjects, fetchCompaniesByLocations]);

  // ── Load last call statuses ───────────────────────────────────────────────
  useEffect(() => {
    if (!fetchLastCallStatuses) return;

        if (!selectedCallCategory || selectedCallCategory === "All") {
      setLastCallStatusOptions(["All"]);
      setSelectedLastCallStatus("All");
      setFetchingStatuses(false);
      return;
    }

    let alive = true;
    (async () => {
      try {
        setFetchingStatuses(true);
        setStatusFilterError(false);
        const data = await fetchLastCallStatuses(selectedCallCategory);

        let statusList = ["All"];
        const extract = (arr) =>
          arr.map(item =>
            typeof item === "object" && item !== null
              ? item.label || item.key || item.name || item.status
              : item
          ).filter(Boolean);

        if (data?.success && Array.isArray(data.statuses) && data.statuses.length)
          statusList = ["All", ...extract(data.statuses)];
        else if (Array.isArray(data?.data) && data.data.length)
          statusList = ["All", ...extract(data.data)];
        else if (Array.isArray(data) && data.length)
          statusList = ["All", ...extract(data)];
        else
          setStatusFilterError(true);

        statusList = [...new Set(statusList)];
        setLastCallStatusOptions(statusList.length > 1 ? statusList : ["All"]);
        setSelectedLastCallStatus("All");
      } catch (e) {
        console.error("Failed to load last call statuses:", e);
        setStatusFilterError(true);
        setLastCallStatusOptions(["All"]);
      } finally {
        if (alive) setFetchingStatuses(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchLastCallStatuses,selectedCallCategory]);

  useEffect(() => {
    if (!fetchLastCallSubStatuses) return;

    // No status selected → nothing to fetch, reset substatus state
    if (!selectedLastCallStatus || selectedLastCallStatus === "All") {
      setLastCallSubStatusOptions(["All"]);
      setSelectedLastCallSubStatus("All");
      setFetchingSubStatuses(false);
      return;
    }

    let alive = true;
    (async () => {
      try {
        setFetchingSubStatuses(true);
        setSubStatusFilterError(false);
       const data = await fetchLastCallSubStatuses("",selectedLastCallStatus);

        let statusList = ["All"];
        const extract = (arr) =>
          arr.map(item =>
            typeof item === "object" && item !== null
              ? item.label || item.key || item.name || item.status
              : item
          ).filter(Boolean);

        if (data?.success && Array.isArray(data.statuses) && data.statuses.length)
          statusList = ["All", ...extract(data.statuses)];
        else if (Array.isArray(data?.data) && data.data.length)
          statusList = ["All", ...extract(data.data)];
        else if (Array.isArray(data) && data.length)
          statusList = ["All", ...extract(data)];
        else
          setSubStatusFilterError(true);

        statusList = [...new Set(statusList)];
        setLastCallSubStatusOptions(statusList.length > 1 ? statusList : ["All"]);
        setSelectedLastCallSubStatus("All");
      } catch (e) {
        console.error("Failed to load last call sub statuses:", e);
        setSubStatusFilterError(true);
        setLastCallSubStatusOptions(["All"]);
      } finally {
        if (alive) setFetchingSubStatuses(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchLastCallSubStatuses, selectedLastCallStatus]);

  // ── Reset helper (clears results; caller handles workers/total state) ─────
  const resetResults = useCallback((onReset) => {
    if (onReset) onReset();
  }, []);

  // ── Clear all filters ─────────────────────────────────────────────────────
  const clearAllFilters = useCallback((onReset) => {
    setSelectedLocations(locationOptions.filter(o => o !== "(Blanks)"));
    setSelectedSkills(skillOptions);
    setSelectedWorkerTypes(["Registered", "Unregistered"]);
    setSelectedGenders(["(Blanks)", "Male", "Female", "Others"]);
    setSelectedSources(sourceOptions.filter(s => s !== "(Blanks)"));
    setSelectedAadhaar("All");
    setHighestqualification("All")
    setSelectedKyc("All");
    setSelectedRefStatus("All");
    setSelectedCallCategory("All");
    setSelectedCompanyCheck("All");
    setSelectedBlacklisted("All");
    setselectedpaymentverification("All");
    setSelectedLastCompanies([]);
    setSelectedAllTimeCompanies([]);
    setSelectedLastCallStatus("All");
    setActiveCompanyFilter(null);
    setRegisteredOnRange({ startDate: "", endDate: "" });
    setLastWorkedOnRange({ startDate: "", endDate: "" });
    setCompanyLastWorkedOnRange({ startDate: "", endDate: "" });
    setSelectedHasAppToken("All");
    setShiftCountRange({ min: "", max: "" });
    
    // ── Clear new filters ──
    setSelectedPoliceVerification("All");
    setSelectedMarksheet("All");  // ← Keep as marksheet
    setSelectedEshram("All");
    
    if (onReset) onReset();
  }, [locationOptions, skillOptions, sourceOptions]);

  // ─────────────────────────────────────────────────────────────────────────
  // PAYLOAD BUILDER A — WorkerOnboarding  (JSON body, paginated)
  // ─────────────────────────────────────────────────────────────────────────
  const buildOnboardingPayload = useCallback((page = 1, overrides = {}) => {
    const {
      locations      = selectedLocations,
      skills         = selectedSkills,
      workerTypes    = selectedWorkerTypes,
      genders        = selectedGenders,
      sources        = selectedSources,
      aadhaar        = selectedAadhaar,
    qualification  = Highestqualification,
      kyc            = selectedKyc,
      refStatus      = selectedRefStatus,
      callCategory   = selectedCallCategory,
      companyCheck   = selectedCompanyCheck,
      blacklisted    = selectedBlacklisted,
      paymentverification = selectedpaymentverification,
      lastCompanies = selectedLastCompanies,
      allTimeCompanies = selectedAllTimeCompanies,
      activeCompany = activeCompanyFilter,
      regRange = registeredOnRange,
      lwRange = lastWorkedOnRange,
      companyLwRange = companyLastWorkedOnRange,
      lastCallStatus = selectedLastCallStatus,
      lastCallSubStatus=selectedLastCallSubStatus,

      policeVerification = selectedPoliceVerification,
      marksheet = selectedMarksheet,  // ← Keep as marksheet
      eshram = selectedEshram,
    } = overrides;

    const payload = { page, limit: PAGE_LIMIT };

    // Location IDs
    const locationIds = locations
      .map(name => name === "(Blanks)" ? "blank" : locationObjects.find(l => l.name === name)?._id)
      .filter(Boolean);
    const allLocationsSelected = locations.length === locationOptions.length && locationOptions.length > 0;
    if (locationIds.length && !allLocationsSelected) payload.locationIds = locationIds;

    // Skill IDs
    const skillIds = skills
      .map(name => name === "(Blanks)" ? "blank" : skillObjects.find(s => s.name === name)?._id)
      .filter(Boolean);
    const allSkillsSelected = skills.length === skillOptions.length && skillOptions.length > 0;
    if (skillIds.length && !allSkillsSelected) payload.skillIds = skillIds;

    // ── Company filters (Updated) ──
    const activeSelections = activeCompany === "last" ? lastCompanies :
      activeCompany === "allTime" ? allTimeCompanies : [];

    // Send company IDs only when not all companies are selected
    if (activeSelections.length > 0) {
      const allCompaniesSelected = activeSelections.length === companyOptions.length && companyOptions.length > 0;

      if (!allCompaniesSelected) {
        const companyIds = activeSelections
          .map(selection => {
            if (selection === "blank") return "blank";
            return companyObjects.find(c => c._id === selection)?._id;
          })
          .filter(Boolean);

        if (companyIds.length > 0) {
          payload.companyIds = companyIds;

          if (activeCompany === "last") {
            payload.lastCompany = true;
          } else if (activeCompany === "allTime") {
            payload.allTimeCompany = true;
          }
        }
      } else {
        if (activeCompany === "last") {
          payload.lastCompany = true;
        } else if (activeCompany === "allTime") {
          payload.allTimeCompany = true;
        }
      }
    }

    // Company check status
    if (activeSelections.length === 1 && companyCheck !== "All") {
      if (companyCheck === "Cleared") payload.companyChecked = true;
      if (companyCheck === "Pending") payload.companyChecked = false;
      if (companyCheck === "Blacklisted") payload.isBlacklisted = true;
    }

    // Company last worked on
    if (activeSelections.length === 1) {
      if (companyLwRange.startDate) payload.companyLastWorkedFrom = companyLwRange.startDate;
      if (companyLwRange.endDate) payload.companyLastWorkedTo = companyLwRange.endDate;
    }

    // Worker type
    const allTypes = ["Registered", "Unregistered"];
    if (!allTypes.every(t => workerTypes.includes(t))) {
      if (workerTypes.includes("Registered") && !workerTypes.includes("Unregistered")) payload.workerType = "registered";
      if (!workerTypes.includes("Registered") && workerTypes.includes("Unregistered")) payload.workerType = "not-registered";
    }

    // Gender
    const genderMap = { Male: "male", Female: "female", Others: "others", "(Blanks)": "blank" };
    const allGenders = ["male", "female", "others", "blank"];
    const filteredGenders = genders.map(g => genderMap[g]).filter(Boolean);
    if (!allGenders.every(g => filteredGenders.includes(g)) && filteredGenders.length > 0)
      payload.gender = filteredGenders.length === 1 ? filteredGenders[0] : filteredGenders;

    // Source
    const sourceMap = {};
    sourceOptions.forEach(opt => {
      if (opt !== "(Blanks)") {
        sourceMap[opt] = opt;
      }
    });
    sourceMap["(Blanks)"] = "blank";

    const allSources = sourceOptions.filter(s => s !== "(Blanks)");
    const allSourcesSelected = allSources.every(s => sources.includes(s));

    if (!allSourcesSelected && sources.length > 0) {
      const mapped = sources.map(s => sourceMap[s] || s).filter(Boolean);
      payload.source = mapped.length === 1 ? mapped[0] : mapped;
    }

    // Simple boolean / string filters
    if (aadhaar  === "Yes")     payload.aadhaarVerified = true;
    if (aadhaar  === "No")      payload.aadhaarVerified = false;
    if (qualification && qualification !== "All") {
  payload.highestQualification = qualification;
}
    if (kyc      === "Done")    payload.kyc             = true;
    if (kyc      === "Pending") payload.kyc             = false;
    if (refStatus === "Done")   payload.refCheck        = "done";
    if (refStatus === "Pending") payload.refCheck       = "not";
    if (callCategory && callCategory !== "All") payload.category = callCategory;

    if (!payload.isBlacklisted) {
      if (blacklisted === "Yes") payload.isBlacklisted = true;
      if (blacklisted === "No") payload.isBlacklisted = false;
    }

    // ── Payment Verification Status ──
    if (paymentverification && paymentverification !== "All") {
      if (paymentverification === "failed") {
        payload.nameVerificationStatus = "failed";
      } else if (paymentverification === "success") {
        payload.nameVerificationStatus = "success";
      } else if (paymentverification === "not checked") {
        payload.nameVerificationStatus = "not checked";
      }
    }

    if (lastCallStatus && lastCallStatus !== "All") payload.lastStatus = lastCallStatus;
    if (lastCallSubStatus && lastCallSubStatus !== "All") payload.lastSubStatus = lastCallSubStatus;

    // Date ranges
    if (regRange.startDate) payload.registeredDateFrom = regRange.startDate;
    if (regRange.endDate) payload.registeredDateTo = regRange.endDate;
    if (lwRange.startDate) payload.lastWorkedFrom = lwRange.startDate;
    if (lwRange.endDate) payload.lastWorkedTo = lwRange.endDate;

    if (selectedHasAppToken === "Yes") payload.hasAppToken = true;
    if (selectedHasAppToken === "No") payload.hasAppToken = false;

    if (shiftCountRange.min !== "" && shiftCountRange.min !== null) payload.shiftCountMin = Number(shiftCountRange.min);
    if (shiftCountRange.max !== "" && shiftCountRange.max !== null) payload.shiftCountMax = Number(shiftCountRange.max);

    // ─── Police Verification ──────────────────────────────────────────────
    if (policeVerification && policeVerification !== "All") {
      let statusValue = null;
      
      if (typeof policeVerification === 'object' && policeVerification !== null) {
        statusValue = policeVerification.status || policeVerification.value || policeVerification.key || null;
      } else if (typeof policeVerification === 'string') {
        statusValue = policeVerification;
      } else {
        statusValue = String(policeVerification);
      }

      if (statusValue) {
        const normalized = statusValue.toLowerCase().trim();
        
        if (normalized === 'pass') {
          payload.policeVerification = 'pass';
        } else if (normalized === 'failed' || normalized === 'fail') {
          payload.policeVerification = 'failed';
        } else if (normalized === 'not verified') {
          payload.policeVerification = 'not verified';
        } else if (normalized === 'not uploaded') {
          payload.policeVerification = 'not uploaded';
        } else if (normalized === 'verified') {
          payload.policeVerification = 'verified';
        } else if (normalized === 'done') {
          payload.policeVerification = 'done';
        } else {
          payload.policeVerification = statusValue;
        }
      }
    }

    // ─── Marksheet (keep as marksheet) ──────────────────────────────────────
    if (marksheet && marksheet !== "All") {
      let statusValue = null;
      
      if (typeof marksheet === 'object' && marksheet !== null) {
        statusValue = marksheet.status || marksheet.value || marksheet.key || null;
      } else if (typeof marksheet === 'string') {
        statusValue = marksheet;
      } else {
        statusValue = String(marksheet);
      }

      if (statusValue) {
        const normalized = statusValue.toLowerCase().trim();
        
        if (normalized === 'pass') {
          payload.marksheet = 'pass';
        } else if (normalized === 'failed' || normalized === 'fail') {
          payload.marksheet = 'failed';
        } else if (normalized === 'not verified') {
          payload.marksheet = 'not verified';
        } else if (normalized === 'not uploaded') {
          payload.marksheet = 'not uploaded';
        } else if (normalized === 'verified') {
          payload.marksheet = 'verified';
        } else if (normalized === 'done') {
          payload.marksheet = 'done';
        } else {
          payload.marksheet = statusValue;
        }
      }
    }

    // eShram (boolean)
    if (eshram && eshram !== "All") {
      payload.eshram = eshram === "Yes";
    }

    return payload;
  }, [
    selectedLocations, selectedSkills, selectedWorkerTypes, selectedGenders,
    selectedSources, selectedAadhaar,
    Highestqualification,
     selectedKyc, selectedRefStatus,
    selectedCompanyCheck, selectedBlacklisted, selectedpaymentverification,
    selectedLastCompanies, selectedAllTimeCompanies, activeCompanyFilter,
    registeredOnRange, lastWorkedOnRange, companyLastWorkedOnRange, selectedLastCallStatus,selectedLastCallSubStatus,selectedCallCategory,
    locationObjects, skillObjects, locationOptions, skillOptions, companyObjects, companyOptions, sourceOptions,
    selectedHasAppToken, shiftCountRange,
    selectedPoliceVerification, selectedMarksheet, selectedEshram,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // PAYLOAD BUILDER B — SuperAdminDashboard  (query-string params object)
  // ─────────────────────────────────────────────────────────────────────────
  const buildDashboardParams = useCallback((overrides = {}) => {
    const {
      locations          = selectedLocations,
      skills             = selectedSkills,
      workerTypes        = selectedWorkerTypes,
      genders            = selectedGenders,
      sources            = selectedSources,
      aadhaar            = selectedAadhaar,
      qualification  = Highestqualification,
      kyc                = selectedKyc,
      refStatus          = selectedRefStatus,
      callCategory   = selectedCallCategory,
      companyCheck       = selectedCompanyCheck,
      blacklisted        = selectedBlacklisted,
      paymentverification = selectedpaymentverification,
      lastCompanies      = selectedLastCompanies,
      allTimeCompanies   = selectedAllTimeCompanies,
      activeCompany      = activeCompanyFilter,
      regRange           = registeredOnRange,
      lwRange            = lastWorkedOnRange,
      companyLwRange     = companyLastWorkedOnRange,
      lastCallStatus     = selectedLastCallStatus,
      lastCallSubStatus=selectedLastCallSubStatus,
      policeVerification = selectedPoliceVerification,
      marksheet = selectedMarksheet,  // ← Keep as marksheet
      eshram = selectedEshram,
    } = overrides;

    // Helper: convert array to comma-separated string or "all"
    const toParam = (arr, allOption, allCheck) => {
      if (allCheck) return "all";
      if (!arr.length) return "";
      return arr.join(",");
    };

    // Location IDs and names
    const locationIds = locations
      .map(name => name === "(Blanks)" ? "blank" : locationObjects.find(l => l.name === name)?._id)
      .filter(Boolean);
    const allLocationsSelected = locations.length === locationOptions.length && locationOptions.length > 0;

    // Skill IDs and names
    const skillIds = skills
      .map(name => name === "(Blanks)" ? "blank" : skillObjects.find(s => s.name === name)?._id)
      .filter(Boolean);
    const allSkillsSelected = skills.length === skillOptions.length && skillOptions.length > 0;

    // Worker types
    const allTypesSelected = workerTypes.length === 2; // Registered + Unregistered

    // Genders
    const allGendersSelected = genders.length === 4; // All 4 options

    // Sources
    const allSourcesSelected = sources.length === sourceOptions.filter(s => s !== "(Blanks)").length;

    // Build params object
    const params = {};

    // ── Required filters (same as onboarding) ──
    params.locationId = allLocationsSelected ? "all" : locationIds.join(",");
    params.skillIds = allSkillsSelected ? "all" : skillIds.join(",");
    params.workerType = allTypesSelected ? "all" : workerTypes.map(t => t.toLowerCase()).join(",");
    params.gender = allGendersSelected ? "all" : genders.map(g => g === "(Blanks)" ? "blank" : g).join(",");
    params.source = allSourcesSelected ? "all" : sources.map(s => s === "(Blanks)" ? "blank" : s).join(",");

    // ── Date ranges ──
    const showRegisteredDate = workerTypes.includes("Registered");
    if (showRegisteredDate && regRange.startDate) params.startDate = regRange.startDate;
    if (showRegisteredDate && regRange.endDate) params.endDate = regRange.endDate;

    // ── Company filters ──
    const activeSelections = activeCompany === "last" ? lastCompanies :
      activeCompany === "allTime" ? allTimeCompanies : [];

    if (activeSelections.length > 0) {
      const allCompaniesSelected = activeSelections.length === companyOptions.length && companyOptions.length > 0;

      if (!allCompaniesSelected) {
        const companyIds = activeSelections
          .map(selection => {
            if (selection === "blank") return "blank";
            return companyObjects.find(c => c._id === selection)?._id;
          })
          .filter(Boolean);

        if (companyIds.length > 0) {
          params.companyIds = companyIds.join(",");

          if (activeCompany === "last") {
            params.lastCompany = "true";
          } else if (activeCompany === "allTime") {
            params.allTimeCompany = "true";
          }
        }
      } else {
        if (activeCompany === "last") {
          params.lastCompany = "true";
        } else if (activeCompany === "allTime") {
          params.allTimeCompany = "true";
        }
      }
    }

    // ── Aadhaar Verified ──
    if (aadhaar === "Yes") params.aadhaarVerified = "true";
    if (aadhaar === "No") params.aadhaarVerified = "false";

if (qualification && qualification !== "All") {
  params.highestQualification = qualification;
}
    // ── KYC Status ──
    if (kyc === "Done") params.kyc = "true";
    if (kyc === "Pending") params.kyc = "false";

    // ── Reference Check Status ──
    if (refStatus === "Done") params.refCheck = "done";
    if (refStatus === "Pending") params.refCheck = "not";

    if (callCategory && callCategory !== "All") params.category = callCategory;

    

    // ── Company Check Status ──
    if (activeSelections.length === 1 && companyCheck !== "All") {
      if (companyCheck === "Employed") params.companyChecked = "true";
      if (companyCheck === "Not Employed") params.companyChecked = "false";
    }

    if (blacklisted === "Yes") params.isBlacklisted = "true";
    if (blacklisted === "No") params.isBlacklisted = "false";

    if (paymentverification && paymentverification !== "All") {
      if (paymentverification === "failed") {
        params.paymentverification = "failed";
      } else if (paymentverification === "success") {
        params.paymentverification = "success";
      } else if (paymentverification === "not checked") {
        params.paymentverification = "not checked";
      }
    }

    // ── Last Call Status ──
    if (lastCallStatus && lastCallStatus !== "All") params.lastStatus = lastCallStatus;
    if (lastCallSubStatus && lastCallSubStatus !== "All") params.lastStatus = lastCallSubStatus;


    // ── Last Worked On (global) ──
    if (lwRange.startDate) params.lastWorkedFrom = lwRange.startDate;
    if (lwRange.endDate) params.lastWorkedTo = lwRange.endDate;

    // ── Company Last Worked On (company-specific) ──
    if (activeSelections.length === 1) {
      if (companyLwRange.startDate) params.companyLastWorkedFrom = companyLwRange.startDate;
      if (companyLwRange.endDate) params.companyLastWorkedTo = companyLwRange.endDate;
    }

    if (selectedHasAppToken === "Yes") params.hasAppToken = "true";
    if (selectedHasAppToken === "No") params.hasAppToken = "false";

    if (shiftCountRange.min !== "" && shiftCountRange.min !== null) params.shiftCountMin = String(shiftCountRange.min);
    if (shiftCountRange.max !== "" && shiftCountRange.max !== null) params.shiftCountMax = String(shiftCountRange.max);

    // ─── Police Verification ──────────────────────────────────────────────
    if (policeVerification && policeVerification !== "All") {
      let statusValue = null;
      
      if (typeof policeVerification === 'object' && policeVerification !== null) {
        statusValue = policeVerification.status || policeVerification.value || policeVerification.key || null;
      } else if (typeof policeVerification === 'string') {
        statusValue = policeVerification;
      } else {
        statusValue = String(policeVerification);
      }

      if (statusValue) {
        const normalized = statusValue.toLowerCase().trim();
        
        if (normalized === 'pass') {
          params.policeVerification = 'pass';
        } else if (normalized === 'failed' || normalized === 'fail') {
          params.policeVerification = 'failed';
        } else if (normalized === 'not verified') {
          params.policeVerification = 'not verified';
        } else if (normalized === 'not uploaded') {
          params.policeVerification = 'not uploaded';
        } else if (normalized === 'verified') {
          params.policeVerification = 'verified';
        } else if (normalized === 'done') {
          params.policeVerification = 'done';
        } else {
          params.policeVerification = statusValue;
        }
      }
    }

    // ─── Marksheet (keep as marksheet) ──────────────────────────────────────
    if (marksheet && marksheet !== "All") {
      let statusValue = null;
      
      if (typeof marksheet === 'object' && marksheet !== null) {
        statusValue = marksheet.status || marksheet.value || marksheet.key || null;
      } else if (typeof marksheet === 'string') {
        statusValue = marksheet;
      } else {
        statusValue = String(marksheet);
      }

      if (statusValue) {
        const normalized = statusValue.toLowerCase().trim();
        
        if (normalized === 'pass') {
          params.marksheet = 'pass';
        } else if (normalized === 'failed' || normalized === 'fail') {
          params.marksheet = 'failed';
        } else if (normalized === 'not verified') {
          params.marksheet = 'not verified';
        } else if (normalized === 'not uploaded') {
          params.marksheet = 'not uploaded';
        } else if (normalized === 'verified') {
          params.marksheet = 'verified';
        } else if (normalized === 'done') {
          params.marksheet = 'done';
        } else {
          params.marksheet = statusValue;
        }
      }
    }

    // eShram (boolean)
    if (eshram && eshram !== "All") {
      params.eshram = eshram === "Yes" ? "true" : "false";
    }

    // Remove any empty params
    Object.keys(params).forEach(key => {
      if (params[key] === "" || params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    return params;
  }, [
    selectedLocations, selectedSkills, selectedWorkerTypes, selectedGenders,
    selectedSources, selectedAadhaar,
    Highestqualification, 
    selectedKyc, selectedRefStatus,
    selectedCompanyCheck, selectedBlacklisted, selectedpaymentverification,
    selectedLastCompanies, selectedAllTimeCompanies, activeCompanyFilter,
    registeredOnRange, lastWorkedOnRange, companyLastWorkedOnRange, selectedLastCallStatus,
    locationObjects, skillObjects, locationOptions, skillOptions, companyObjects, companyOptions, sourceOptions,
    selectedHasAppToken, shiftCountRange,
    selectedPoliceVerification, selectedMarksheet, selectedEshram,
  ]);

  // ── Expose everything the UI panels need ──────────────────────────────────
  return {
    // option arrays
    locationOptions, skillOptions, companyOptions, sourceOptions, lastCallStatusOptions,lastCallSubStatusOptions,

    // loading flags
    fetchingLocations, fetchingSkills, fetchingCompanies, fetchingSources, fetchingStatuses,fetchingSubStatuses, statusFilterError,subStatusFilterError,

    // filter values
    selectedLocations, setSelectedLocations,
    selectedSkills,    setSelectedSkills,
    selectedWorkerTypes,    setSelectedWorkerTypes,
    selectedGenders,        setSelectedGenders,
    selectedSources,        setSelectedSources,
    selectedAadhaar,        setSelectedAadhaar,
    Highestqualification,setHighestqualification,
    selectedKyc,            setSelectedKyc,
    selectedRefStatus,      setSelectedRefStatus,
    selectedCompanyCheck,   setSelectedCompanyCheck,
    selectedBlacklisted,    setSelectedBlacklisted,
    selectedpaymentverification, setselectedpaymentverification,
    selectedLastCompanies,  setSelectedLastCompanies,
    selectedAllTimeCompanies, setSelectedAllTimeCompanies,
    selectedLastCallStatus, setSelectedLastCallStatus,
    selectedLastCallSubStatus, setSelectedLastCallSubStatus,
    selectedCallCategory,   setSelectedCallCategory,
    registeredOnRange,      setRegisteredOnRange,
    lastWorkedOnRange,      setLastWorkedOnRange,
    companyLastWorkedOnRange, setCompanyLastWorkedOnRange,
    activeCompanyFilter,    setActiveCompanyFilter,

    // ─── NEW FILTER VALUES ──────────────────────────────────────────────────
    selectedPoliceVerification, setSelectedPoliceVerification,
    selectedMarksheet, setSelectedMarksheet,  // ← Keep as marksheet
    selectedEshram, setSelectedEshram,

    // derived
    companyFiltersEnabled,
    activeCompanySelections,
    companyCheckEnabled,
    selectedCompanyName,

    // helpers
    clearAllFilters,
    resetResults,

    // payload builders
    buildOnboardingPayload,
    buildDashboardParams,

    selectedHasAppToken, 
    setSelectedHasAppToken,
    shiftCountRange,
    setShiftCountRange,
  };
}